import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../../hooks/useI18n";
import { theme } from "../../../layout/Theme";
import { ImportProgress } from "./types";

export function LoadingView() {
  const { t } = useI18n();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}>
      <CircularProgress />
      <Typography>{t("evc_onboarding.parsing")}</Typography>
    </Box>
  );
}

export function ImportErrorView({ errorMessage, onSkip }: { errorMessage: string | null; onSkip: () => void }) {
  const { t } = useI18n();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Alert severity="error">{errorMessage || t("evc_onboarding.parse_error")}</Alert>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={onSkip}>
          {t("evc_onboarding.continue_without_import")}
        </Button>
      </Box>
    </Box>
  );
}

export function ImportingView({ importProgress }: { importProgress: ImportProgress }) {
  const { t } = useI18n();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}>
      <CircularProgress />
      <Typography>
        {t("evc_onboarding.importing_progress", {
          done: importProgress.done,
          total: importProgress.total,
        })}
      </Typography>
    </Box>
  );
}

export function ImportDoneView({
  importProgress,
  viewRecordPath,
}: {
  importProgress: ImportProgress;
  viewRecordPath: string | null;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleViewRecord = () => {
    if (viewRecordPath) {
      navigate(viewRecordPath, { replace: true });
    } else {
      navigate("..", { relative: "path", replace: true });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, py: 6 }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 64 }} color="success" />
      <Typography variant="h6">{t("evc_onboarding.success_title")}</Typography>
      <Typography color={theme.palette.neutral[600]}>
        {importProgress.errors > 0
          ? t("evc_onboarding.success_with_errors", {
              imported: importProgress.done - importProgress.errors,
              errors: importProgress.errors,
            })
          : t("evc_onboarding.success_message", { count: importProgress.done })}
      </Typography>
      <Button variant="contained" size="large" onClick={handleViewRecord} disableElevation>
        {t("evc_onboarding.view_health_record")}
      </Button>
    </Box>
  );
}
